import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { formatarData, formatarPlaca } from 'src/app/core/utils/utilities';
import {
  IGetRelatorioPlacasCaixaItem,
  IGetRelatorioPlacasCaixaListParams,
  IGetRelatorioPlacasCaixaListResponse
} from 'src/app/services/ReportService/report.model';
import { ReportService } from 'src/app/services/ReportService/report.service';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { IGetServico } from 'src/app/services/ServicoService/servico.model';
import { ServicoService } from 'src/app/services/ServicoService/servico.service';

type ServicoOption = {
  name: string;
  value: number | null;
};

@Component({
  selector: 'app-relatorio-placas-caixa',
  templateUrl: './relatorio-placas-caixa.component.html',
  styleUrls: ['./relatorio-placas-caixa.component.scss']
})
export class RelatorioPlacasCaixaComponent implements OnInit, AfterViewInit {
  form = this.fb.group({
    PK_CAIX: [null as number | null],
    NM_FUNC: [null as string | null],
    FK_SRVC: [null as number | null],
    DT_INIC: [null as string | null],
    DT_FIM: [null as string | null]
  });

  servicoOptions: ServicoOption[] = [{ name: 'Todos', value: null }];

  isLoading = false;
  numeroPagina = 0;
  maxPages = 1;
  tableData: Array<any> = [];

  pesquisaParams = {
    PAGE_NUMBER: 1,
    PAGE_SIZE: 20
  };

  tableColumns = [
    { header: 'Nº Caixa', key: 'numeroCaixa', type: 'text' },
    { header: 'Placa', key: 'placa', type: 'text' },
    { header: 'Serviço', key: 'servico', type: 'text' },
    { header: 'Data', key: 'data', type: 'text' },
    { header: 'Valor', key: 'valor', type: 'text' },
    { header: 'Forma de Pagamento', key: 'formaPagamento', type: 'text' },
    { header: 'Operador', key: 'operador', type: 'text' }
  ];

  constructor(
    private fb: FormBuilder,
    private reportService: ReportService,
    private appExportBase64: AppExportBase64,
    private servicoService: ServicoService
  ) {}

  ngOnInit(): void {
    this.loadServicos();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.pesquisaParams.PAGE_NUMBER = 1;
      this.numeroPagina = 0;
      this.loadData();
    });
  }

  mudarPagina(pagina: number): void {
    this.pesquisaParams.PAGE_NUMBER = pagina + 1;
    this.numeroPagina = pagina;
    this.loadData();
  }

  private createPayload(): IGetRelatorioPlacasCaixaListParams {
    const fkSrvc = Number(this.form.controls.FK_SRVC.value);

    return {
      PK_CAIX: this.form.controls.PK_CAIX.value ?? undefined,
      NM_FUNC: this.form.controls.NM_FUNC.value ?? undefined,
      FK_SRVC: !Number.isNaN(fkSrvc) && fkSrvc > 0 ? fkSrvc : undefined,
      DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
      DT_FIM: this.form.controls.DT_FIM.value ?? undefined,
      PAGE_NUMBER: this.pesquisaParams.PAGE_NUMBER,
      PAGE_SIZE: this.pesquisaParams.PAGE_SIZE
    };
  }

  private loadServicos(): void {
    this.servicoService.GetServico().subscribe({
      next: response => {
        this.servicoOptions = [
          { name: 'Todos', value: null },
          ...response.map((servico: IGetServico) => ({
            name: servico.TX_TIPO_SRVC,
            value: servico.PK_SRVC
          }))
        ];
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetRelatorioPlacasCaixa(payload).subscribe({
      next: (response: IGetRelatorioPlacasCaixaListResponse) => {
        this.tableData = response.ITEMS.map((item: IGetRelatorioPlacasCaixaItem) => ({
          numeroCaixa: item.PK_CAIX ?? '---',
          placa: formatarPlaca(item.TX_PLCA_CAVA) ?? '---',
          servico: item.TX_TIPO_SRVC ?? '---',
          data: item.DT_PGMT_SAID ? formatarData(item.DT_PGMT_SAID) : '---',
          valor: item.VL_PGMT_SAID_CAIX
            ? `R$ ${item.VL_PGMT_SAID_CAIX.toFixed(2).replace('.', ',')}`
            : 'R$ 0,00',
          formaPagamento: this.getFormaPagamento(item.NU_TIPO_PGMT_CAIX),
          operador: item.NM_FUNC ?? '---'
        }));
        this.maxPages = response.TOTAL_PAGES;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarExcel(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetReportPlacasCaixaExcel({
      PK_CAIX: payload.PK_CAIX,
      NM_FUNC: payload.NM_FUNC,
      FK_SRVC: payload.FK_SRVC,
      DT_INIC: payload.DT_INIC,
      DT_FIM: payload.DT_FIM
    }).subscribe({
      next: response => {
        this.appExportBase64.downloadExcel(response.ReportBase64, 'Relatório Placas Caixa.xlsx');
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarPdf(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetReportPlacasCaixaPdf({
      PK_CAIX: payload.PK_CAIX,
      NM_FUNC: payload.NM_FUNC,
      FK_SRVC: payload.FK_SRVC,
      DT_INIC: payload.DT_INIC,
      DT_FIM: payload.DT_FIM
    }).subscribe({
      next: response => {
        this.appExportBase64.downloadPDF(response.ReportBase64, 'Relatório Placas Caixa.pdf');
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private getFormaPagamento(tipo: number | null | undefined): string {
    switch (tipo) {
      case 1:
        return 'Cartão de Crédito';
      case 19:
        return 'Cartão de Débito';
      case 27:
        return 'PIX';
      case 28:
        return 'A Prazo';
      case 9:
        return 'Dinheiro';
      default:
        return '---';
    }
  }

}
